import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { AlertTriangle, CheckCircle, XCircle, Shield, Zap, FileText } from 'lucide-react';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
}

const RiskGradeColors = {
  'AAA': 'bg-emerald-100 text-emerald-800',
  'AA': 'bg-emerald-100 text-emerald-700',
  'A': 'bg-green-100 text-green-700',
  'BBB': 'bg-blue-100 text-blue-700',
  'BB': 'bg-amber-100 text-amber-700',
  'B': 'bg-orange-100 text-orange-700',
  'CCC': 'bg-red-100 text-red-700',
};

const DecisionColors = {
  'APPROVE': 'bg-emerald-500',
  'APPROVE_WITH_CONDITIONS': 'bg-amber-500',
  'REFER_TO_COMMITTEE': 'bg-orange-500',
  'DECLINE': 'bg-red-500',
};

export function DemoOverviewTab({ demoData, currentResult, recommendation }) {
  const analysisResult = demoData.analysis_result;
  const docsReviewed = demoData.documents_reviewed;
  const structureRec = analysisResult.structure_recommendation;
  
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-blue-600" />AI Recommendation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={`p-4 rounded-lg ${DecisionColors[recommendation.decision] || 'bg-slate-500'} text-white`}>
              <p className="text-sm opacity-90">Decision</p>
              <p className="text-2xl font-bold">{recommendation.decision?.replace(/_/g, ' ')}</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-500">Amount</p>
                <p className="text-xl font-bold">{formatCurrency(recommendation.recommended_amount)}</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-500">Tenor</p>
                <p className="text-xl font-bold">{structureRec.recommended_tenor} mo</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-500">Rate</p>
                <p className="text-xl font-bold">{recommendation.all_in_rate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle>Risk Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-4">
              <p className="text-4xl font-bold">{currentResult.risk_score || analysisResult.overall_score}</p>
              <p className="text-slate-500">/100</p>
              <Badge className={`mt-2 ${RiskGradeColors[currentResult.risk_grade || analysisResult.risk_grade]}`}>
                {currentResult.risk_grade || analysisResult.risk_grade}
              </Badge>
            </div>
            <div className="space-y-2 mt-4">
              {Object.entries(analysisResult.risk_factors).slice(0, 4).map(([k, v]) => (
                <div key={k}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{k.replace(/_/g, ' ')}</span>
                    <span>{v}</span>
                  </div>
                  <Progress value={v} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />Key Risks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {analysisResult.key_risks.slice(0, 4).map((r, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-emerald-600 flex items-center gap-2">
              <Shield className="h-5 w-5" />Mitigants
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {analysisResult.mitigants.slice(0, 4).map((m, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Documents ({docsReviewed.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {docsReviewed.slice(0, 8).map((d, i) => (
              <div key={i} className="p-2 bg-slate-50 rounded flex items-center gap-2">
                <CheckCircle className={`h-4 w-4 ${d.status === 'verified' ? 'text-emerald-500' : 'text-amber-500'}`} />
                <span className="text-xs truncate">{d.name}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default DemoOverviewTab;
